<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import ElixirLogo from './ElixirLogo.vue';

const router = useRouter();
const authStore = useAuthStore();
const isNavbarCollapsed = ref(true);
const isUserMenuOpen = ref(false);

// Computed property for authenticated status
const isAuthenticated = computed(() => authStore.isAuthenticated);
const currentUser = computed(() => authStore.user);
const isUserAdmin = computed(() => authStore.isAdmin);

// Toggle navbar for mobile
const toggleNavbar = () => {
  isNavbarCollapsed.value = !isNavbarCollapsed.value;
};

// Toggle user dropdown menu
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

// Close dropdown when clicking outside
const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

// Sign out user
const signOut = async () => {
  try {
    await authStore.logout();
    router.push({ name: 'home' });
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

// Handle clicks outside the dropdown
onMounted(() => {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const isDropdownClick = target.closest('.user-dropdown-container');
    if (!isDropdownClick && isUserMenuOpen.value) {
      closeUserMenu();
    }
  });
});

// Format display name
const getInitials = computed(() => {
  if (!currentUser.value?.displayName) return 'U';
  return currentUser.value.displayName
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

// First name for greeting
const firstName = computed(() => {
  if (!currentUser.value?.displayName) return 'User';
  return currentUser.value.displayName.split(' ')[0];
});
</script>

<template>
  <nav class="navbar navbar-expand-lg navbar-dark bg-gradient-primary shadow-sm fixed-top soft-navbar">
    <div class="container py-1">
      <!-- Brand -->
      <RouterLink class="navbar-brand d-flex align-items-center" to="/">
        <div class="d-flex align-items-center">
          <img src="/src/assets/elixir-tree.png" alt="Elixir Logo" class="logo-image me-2" style="height: 40px; width: auto;" />
          <span class="brand-text text-white" style="font-family: 'Georgia', serif; font-style: italic;">Elixir Registry</span>
        </div>
      </RouterLink>

      <!-- Mobile toggle - Only visible on smaller screens -->
      <button
        class="navbar-toggler custom-toggler d-lg-none"
        type="button"
        @click="toggleNavbar"
        aria-controls="navbarNav"
        :aria-expanded="!isNavbarCollapsed"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon custom-toggler-icon"></span>
      </button>

      <!-- Navbar links -->
      <div
        class="collapse navbar-collapse d-lg-flex"
        :class="{ show: !isNavbarCollapsed }"
        id="navbarNav"
      >
        <ul class="navbar-nav ms-auto align-items-center">
          <li class="nav-item">
            <RouterLink class="nav-link text-white" style="background: transparent !important;" to="/">Home</RouterLink>
          </li>
          <li class="nav-item">
            <RouterLink class="nav-link text-white" style="background: transparent !important;" :to="{ name: 'HowItWorks' }">How It Works</RouterLink>
          </li>
          <li class="nav-item">
            <RouterLink class="nav-link text-white" style="background: transparent !important;" :to="{ name: 'FindRegistry' }">Find a Registry</RouterLink>
          </li>
          <li class="nav-item">
            <RouterLink class="nav-link btn btn-elixir-green btn-donate rounded-pill btn-glow donate-btn d-flex align-items-center justify-content-center" :to="{ name: 'Donate' }">
              <i class="material-icons align-middle me-1">favorite</i> Donate Now
            </RouterLink>
          </li>

          <!-- Not authenticated -->
          <template v-if="!isAuthenticated">
            <li class="nav-item ms-2">
              <RouterLink class="nav-link text-white" style="background: transparent !important;" :to="{ name: 'WaysToHelp' }">Ways to Help</RouterLink>
            </li>
            <li class="nav-item ms-2">
              <RouterLink class="nav-link text-white" style="background: transparent !important;" :to="{ name: 'SignIn' }">Sign In</RouterLink>
            </li>
          </template>

          <!-- Authenticated - User Dropdown -->
          <template v-else>
            <li class="nav-item ms-2">
              <RouterLink class="nav-link text-white" style="background: transparent !important;" :to="{ name: 'WaysToHelp' }">Ways to Help</RouterLink>
            </li>
            
            <!-- User dropdown menu -->
            <li class="nav-item dropdown user-dropdown-container ms-2">
              <a 
                class="nav-link dropdown-toggle text-white d-flex align-items-center user-dropdown-toggle"
                href="#"
                role="button"
                @click.prevent.stop="toggleUserMenu"
                aria-expanded="false"
              >
                <!-- User avatar/initials -->
                <div v-if="currentUser?.photoURL" class="avatar-container me-2">
                  <img :src="currentUser.photoURL" alt="Profile" class="avatar-img" />
                </div>
                <div v-else class="avatar-container me-2">
                  <div class="avatar-initials">{{ getInitials }}</div>
                </div>
                <span class="d-none d-lg-inline">{{ firstName }}</span>
              </a>
              
              <!-- Dropdown menu -->
              <div class="dropdown-menu dropdown-menu-end" :class="{ show: isUserMenuOpen }">
                <div class="dropdown-header text-center">
                  <div class="font-weight-bold">{{ currentUser?.displayName }}</div>
                  <div class="text-muted small">{{ currentUser?.email }}</div>
                </div>
                <div class="dropdown-divider"></div>
                <RouterLink class="dropdown-item" style="background: transparent !important;" :to="{ name: 'dashboard' }">
                  <i class="material-icons align-middle me-2 small">dashboard</i> Dashboard
                </RouterLink>
                <RouterLink class="dropdown-item" style="background: transparent !important;" :to="{ name: 'profile' }">
                  <i class="material-icons align-middle me-2 small">person</i> Profile
                </RouterLink>
                <RouterLink v-if="isUserAdmin" class="dropdown-item" style="background: transparent !important;" :to="{ name: 'Admin' }">
                  <i class="material-icons align-middle me-2 small">admin_panel_settings</i> Admin
                </RouterLink>
                <div class="dropdown-divider"></div>
                <a class="dropdown-item" href="#" style="background: transparent !important;" @click.prevent="signOut">
                  <i class="material-icons align-middle me-2 small">logout</i> Sign Out
                </a>
              </div>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  transition: all 0.3s ease;
  min-height: 60px;
  padding: 0.4rem 0;
}

.container {
  padding-top: 0;
  padding-bottom: 0;
}

.navbar-brand {
  font-weight: 700;
  background-color: transparent;
  border: none;
  padding: 0;
  margin-right: 1rem;
}

.brand-text {
  font-size: 1.6rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Add top padding to account for fixed navbar */
:deep(body) {
  padding-top: 60px;
}

.nav-link {
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.25rem 0.75rem;
  transition: all 0.2s ease;
  position: relative;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
}

/* Reset any background styling on nav links */
.navbar-nav .nav-item {
  background-color: transparent !important;
}

.navbar-nav .nav-item a {
  background-color: transparent !important;
}

/* Any router-link style adjustments */
.router-link-active:not(.btn) {
  font-weight: 700;
  background-color: transparent !important;
}

.router-link-active:not(.btn)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: white;
  border-radius: 3px;
}

/* Removed hover lines completely */

.btn-elixir-green {
  background-color: var(--elixir-blue);
  color: white;
  font-weight: 600;
  padding: 0.4rem 1rem;
  transition: all 0.3s ease;
  border: 2px solid #37b86f;
  margin-left: 0.5rem;
}

.btn-elixir-green:hover {
  background-color: #2ca25f;
  transform: translateY(-2px);
  border-color: white;
  color: white;
  box-shadow: 0 4px 10px rgba(55, 184, 111, 0.3);
}

.btn-donate {
  padding: 0.35rem 0.9rem;
  height: 38px;
  line-height: 1;
}

/* Fix for the donate button font to match other text */
.donate-btn {
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.9rem;
}

.btn-glow {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.btn-glow:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: inherit;
  z-index: -1;
  transform: translateY(100%) scale(0.5);
  opacity: 0;
  transition: transform 0.3s, opacity 0.3s;
}

.btn-glow:hover:after {
  transform: translateY(0) scale(1);
  opacity: 1;
}

.navbar-toggler {
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0.25rem 0.5rem;
}

.custom-toggler {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  background-color: rgba(255, 255, 255, 0.1);
}

.custom-toggler-icon {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='rgba(255, 255, 255, 0.9)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/%3E%3C/svg%3E");
  width: 1.5em;
  height: 1.5em;
}

/* User dropdown styles */
.dropdown-toggle::after {
  vertical-align: middle;
}

.dropdown-menu {
  padding: 0.5rem 0;
  min-width: 16rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border-radius: 0.5rem;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #333;
}

.dropdown-header {
  padding: 1rem;
  background-color: #f8f9fa;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.dropdown-item {
  padding: 0.6rem 1.5rem;
  display: flex;
  align-items: center;
  color: var(--soft-text-primary);
  text-decoration: none;
}

.dropdown-item:hover {
  background-color: #f0f0f0;
}

.dropdown-divider {
  margin: 0;
}

.font-weight-bold {
  font-weight: 600;
}

/* Avatar styles */
.avatar-container {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  font-weight: 600;
  font-size: 0.875rem;
  color: white;
}

/* Adjust navbar collapse breakpoint */
@media (min-width: 992px) and (max-width: 1199px) {
  .nav-link {
    font-size: 0.9rem;
    padding: 0.25rem 0.5rem;
  }
  
  .btn-donate {
    padding: 0.3rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .navbar-collapse {
    justify-content: flex-end;
  }
}

/* Medium devices (tablets, less than 992px) */
@media (max-width: 991px) {
  .navbar-collapse.collapse:not(.show) {
    display: none !important;
  }
  
  .navbar-collapse {
    background-color: var(--elixir-blue);
    padding: 1rem;
    border-radius: 0.5rem;
    margin-top: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
  
  .navbar-nav {
    padding-top: 0.5rem;
  }
  
  .nav-item {
    margin-bottom: 0.5rem;
  }
  
  .btn-donate {
    margin-top: 0.5rem;
    display: inline-block;
  }
}

/* Extra small devices (phones) */
@media (max-width: 575.98px) {
  .brand-text {
    font-size: 1.1rem;
  }
  
  .logo-image {
    height: 30px !important;
  }
  
  .navbar {
    padding: 0.2rem 0;
  }
}

/* Small devices (landscape phones) */
@media (min-width: 576px) and (max-width: 767.98px) {
  .brand-text {
    font-size: 1.2rem;
  }
  
  .logo-image {
    height: 35px !important;
  }
  
  .navbar {
    padding: 0.2rem 0;
    width: 100%;
    max-width: 100vw;
  }
  
  .container {
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    max-width: 100%;
  }
  
  .navbar-brand {
    max-width: 65%;
    flex-shrink: 1;
  }
  
  .elixir-logo-container {
    max-width: 100%;
  }
  
  .navbar-toggler-icon {
    width: 1.2em;
    height: 1.2em;
  }
}

/* Landscape mode specific styles */
@media (max-height: 500px) and (orientation: landscape) {
  .navbar {
    min-height: 50px;
    padding: 0.2rem 0;
  }
  
  .navbar-brand {
    max-width: 200px;
  }
  
  body {
    padding-top: 50px;
  }
  
  :deep(body) {
    padding-top: 50px;
  }
  
  .brand-text {
    font-size: 1.2rem;
  }
  
  .logo-image {
    height: 30px !important;
  }
}

/* Show dropdown on click for mobile */
@media (max-width: 991px) {
  .dropdown-menu.show {
    position: static !important;
    float: none;
    width: 100%;
    margin-top: 0.5rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  .user-dropdown-container {
    width: 100%;
  }
  
  /* Style the dropdown toggle to be more visible on mobile */
  .user-dropdown-toggle {
    background-color: rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px;
    padding: 0.5rem 1rem !important;
    margin: 0.5rem 0;
  }
  
  .navbar-toggler:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
  }
  
  /* Ensure dropdown items are visible on mobile */
  .dropdown-item {
    color: #333 !important;
    background-color: white !important;
  }
  
  .dropdown-item:hover {
    background-color: #f0f0f0 !important;
  }
  
  /* Ensure dropdown header text is visible */
  .dropdown-header {
    color: #333 !important;
  }
  
  .dropdown-header .text-muted {
    color: #666 !important;
  }
}

/* Large screen styles */
@media (min-width: 992px) {
  .navbar-collapse {
    display: flex !important;
    background: transparent;
    border: none;
    box-shadow: none;
  }
  
  .navbar-nav {
    align-items: center;
  }
  
  .navbar-expand-lg .navbar-nav {
    flex-direction: row;
  }
}
</style>
